import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  InputAdornment,
  Fab,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Chat as ChatIcon,
  Search,
  ExpandMore
} from '@mui/icons-material';

const ChatFloatingPanel = ({ 
  currentUser, 
  onlineUsers = [], 
  allUsers = [], 
  unreadCounts = {}, 
  onUserClick 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Merge all users with online status
  const displayUsers = allUsers
    .filter(u => u.userid !== currentUser.userid) // Exclude self
    .map(u => ({
      ...u,
      isOnline: onlineUsers.some(ou => ou.userid === u.userid),
      unread: unreadCounts[u.userid] || 0
    }))
    .sort((a, b) => {
      // Sort by online status first, then name
      if (a.isOnline === b.isOnline) return (a.firstname || '').localeCompare(b.firstname || '');
      return a.isOnline ? -1 : 1;
    })
    .filter(u => 
      searchTerm === '' || 
      `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u.department || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  if (!isOpen) {
    return (
      <Tooltip title="Staff Messaging & Realtime Presence" placement="left" arrow>
        <Fab 
          aria-label="chat"
          onClick={() => setIsOpen(true)}
          sx={{
            position: 'fixed',
            bottom: { xs: 88, md: 28 },
            right: { xs: 16, md: 28 },
            zIndex: 2147483000,
            bgcolor: '#059669',
            color: '#ffffff',
            boxShadow: '0 8px 24px rgba(5, 150, 105, 0.35)',
            '&:hover': {
              bgcolor: '#047857',
              transform: 'scale(1.08)'
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <Badge badgeContent={totalUnread} color="error">
            <ChatIcon />
          </Badge>
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper
      elevation={12}
      sx={{
        position: 'fixed',
        bottom: { xs: 74, md: 0 },
        right: { xs: 12, md: 24 },
        width: { xs: 'calc(100vw - 24px)', sm: 320 },
        maxWidth: 340,
        height: 480,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '16px 16px 0 0',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        zIndex: 2147483000,
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 1.75, 
          bgcolor: '#059669', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setIsOpen(false)}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <ChatIcon fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ffffff' }}>
            Staff Messaging
          </Typography>
          {totalUnread > 0 && (
            <Chip 
              label={totalUnread} 
              size="small" 
              sx={{ height: 20, minWidth: 20, bgcolor: '#ef4444', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} 
            />
          )}
        </Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} sx={{ color: 'white' }}>
          <ExpandMore />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ p: 1.25, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search staff or office..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: '#ffffff',
              borderRadius: 2,
              fontSize: '0.8125rem'
            }
          }}
        />
      </Box>

      {/* User List */}
      <List sx={{ flex: 1, overflowY: 'auto', p: 0, bgcolor: '#ffffff' }}>
        {displayUsers.length === 0 ? (
          <Box p={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">No staff members found.</Typography>
          </Box>
        ) : (
          displayUsers.map(user => (
            <ListItem 
              key={user.userid} 
              button 
              onClick={() => onUserClick(user)}
              sx={{ 
                borderBottom: '1px solid #f8fafc',
                bgcolor: user.unread > 0 ? '#ecfdf5' : 'transparent',
                py: 1,
                px: 2,
                '&:hover': {
                  bgcolor: '#f1f5f3'
                }
              }}
            >
              <ListItemAvatar sx={{ minWidth: 44 }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  variant="dot"
                  color={user.isOnline ? "success" : "default"}
                  sx={{ 
                    '& .MuiBadge-badge': { 
                      border: '2px solid white',
                      bgcolor: user.isOnline ? '#10b981' : '#cbd5e1'
                    } 
                  }}
                >
                  <Avatar sx={{ bgcolor: 'rgba(5, 150, 105, 0.12)', color: '#059669', width: 34, height: 34, fontSize: '0.8125rem', fontWeight: 700 }}>
                    {user.firstname?.[0]}{user.lastname?.[0]}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={user.unread > 0 ? 700 : 500} color="#0f172a" sx={{ fontSize: '0.8125rem' }}>
                      {user.firstname} {user.lastname}
                    </Typography>
                    {user.unread > 0 && (
                      <Chip label={user.unread} size="small" sx={{ height: 18, minWidth: 18, fontSize: '0.65rem', bgcolor: '#059669', color: '#fff', fontWeight: 700 }} />
                    )}
                  </Box>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem', display: 'block' }}>
                    {user.department || user.position || 'Staff'}
                  </Typography>
                }
              />
            </ListItem>
          ))
        )}
      </List>
    </Paper>
  );
};

export default ChatFloatingPanel;
